import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiUpload, FiMapPin, FiPalette, FiEye, FiArrowRight, FiSkipForward } = FiIcons;

const OnboardingFlow = () => {
  const { user, updateUserOnboarding, dbOperations } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleting, setIsCompleting] = useState(false);
  const [config, setConfig] = useState({
    primaryColor: '#3B82F6',
    secondaryColor: '#EF4444', 
    textColor: '#FFFFFF',
    logo: '',
    customText: `Willkommen in ${user?.cityName}!`,
    showLocation: true,
    showGreeting: true,
    greetingText: 'Viele Grüße aus',
    textBackground: true,
    logoPosition: { x: '2%', y: '2%' },
    logoSize: '20%',
    logoOpacity: 1,
    textPosition: { x: 'center', y: 'bottom' },
    textSize: '1.8vmin',
    textOpacity: 0.9,
    textStyle: 'bold',
    fontFamily: 'Arial',
    locationPosition: { x: 'center', y: 'bottom' },
    locationSize: '2.2vmin',
    locationOpacity: 1,
    locationFontFamily: 'Arial',
    locationColor: '#FFFFFF',
    locationStyle: 'bold'
  });

  const steps = [
    {
      id: 1,
      title: 'Willkommen bei CityShot!',
      description: 'Lassen Sie uns Ihre persönliche Selfie-App einrichten.',
      component: 'welcome'
    },
    {
      id: 2,
      title: 'Stadt-Logo hochladen',
      description: 'Laden Sie Ihr Stadtlogo hoch, um Ihre App zu personalisieren.',
      component: 'logo'
    },
    {
      id: 3,
      title: 'Farben anpassen',
      description: 'Wählen Sie die Farben, die zu Ihrer Stadt passen.',
      component: 'colors'
    },
    {
      id: 4,
      title: 'Text personalisieren',
      description: 'Passen Sie die Texte in Ihrer App an.',
      component: 'text'
    },
    {
      id: 5,
      title: 'Vorschau & Fertigstellung',
      description: 'Sehen Sie sich Ihre App-Vorschau an und schließen Sie die Einrichtung ab.',
      component: 'preview'
    }
  ];

  // Redirect if user is already onboarded
  useEffect(() => {
    if (user?.onboarded) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleFileUpload = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfig(prev => ({ ...prev, logo: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    setCurrentStep(steps.length);
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Save the configuration
      await dbOperations.saveConfig(user.id, {
        ...config,
        _timestamp: Date.now()
      });

      // Mark user as onboarded
      await updateUserOnboarding(true);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const renderStepContent = () => {
    const step = steps.find(s => s.id === currentStep);
    
    switch (step?.component) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <span className="text-4xl">🏙️</span>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Herzlich willkommen, {user?.cityName}!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Wir freuen uns, dass Sie sich für CityShot entschieden haben. 
                In wenigen Schritten richten wir Ihre personalisierte Selfie-App ein.
              </p>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h3 className="font-semibold text-blue-900 mb-2">Was erwartet Sie?</h3>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>✨ Logo-Upload für Ihr Branding</li>
                  <li>🎨 Farbanpassung nach Ihren Wünschen</li>
                  <li>📝 Personalisierte Texte</li>
                  <li>👀 Live-Vorschau Ihrer App</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'logo':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUpload} className="text-2xl text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Stadt-Logo hochladen</h3>
              <p className="text-gray-600">
                Laden Sie Ihr Stadtlogo hoch, um Ihre App zu personalisieren. 
                Unterstützte Formate: PNG, JPG, SVG
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer block"
                >
                  {config.logo ? (
                    <div className="space-y-3">
                      <img 
                        src={config.logo} 
                        alt="Logo Preview" 
                        className="h-24 w-auto mx-auto object-contain"
                      />
                      <p className="text-green-600 font-semibold">
                        <SafeIcon icon={FiCheck} className="inline mr-2" />
                        Logo erfolgreich hochgeladen
                      </p>
                      <p className="text-sm text-gray-500">
                        Klicken Sie hier, um ein anderes Logo zu wählen
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto" />
                      <div>
                        <p className="text-lg font-semibold text-gray-700">Logo hochladen</p>
                        <p className="text-sm text-gray-500">
                          Klicken Sie hier oder ziehen Sie eine Datei hinein
                        </p>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Überspringen - Logo später hinzufügen
                </button>
              </div>
            </div>
          </div>
        );

      case 'colors':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiPalette} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Farben anpassen</h3>
              <p className="text-gray-600">
                Wählen Sie die Farben, die zu Ihrer Stadt und Ihrem Branding passen.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primärfarbe
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <span className="font-mono text-sm text-gray-600">{config.primaryColor}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sekundärfarbe
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.secondaryColor}
                    onChange={(e) => setConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <span className="font-mono text-sm text-gray-600">{config.secondaryColor}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Textfarbe
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.textColor}
                    onChange={(e) => setConfig(prev => ({ ...prev, textColor: e.target.value }))}
                    className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <span className="font-mono text-sm text-gray-600">{config.textColor}</span>
                </div>
              </div>

              {/* Color Presets */}
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-3">Oder wählen Sie eine Vorlage:</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { name: 'Blau', primary: '#3B82F6', secondary: '#EF4444' },
                    { name: 'Grün', primary: '#10B981', secondary: '#F59E0B' },
                    { name: 'Lila', primary: '#8B5CF6', secondary: '#EC4899' }
                  ].map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => setConfig(prev => ({ 
                        ...prev, 
                        primaryColor: preset.primary, 
                        secondaryColor: preset.secondary 
                      }))}
                      className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <div className="flex gap-1 mb-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: preset.primary }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: preset.secondary }}
                        />
                      </div>
                      <span className="text-xs text-gray-600">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Texte personalisieren</h3>
              <p className="text-gray-600">
                Passen Sie die Texte an, die in Ihren Selfies angezeigt werden.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Willkommenstext
                </label>
                <input
                  type="text"
                  value={config.customText}
                  onChange={(e) => setConfig(prev => ({ ...prev, customText: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Willkommen in München!"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grußtext
                </label>
                <input
                  type="text"
                  value={config.greetingText}
                  onChange={(e) => setConfig(prev => ({ ...prev, greetingText: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Viele Grüße aus"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showLocation"
                  checked={config.showLocation}
                  onChange={(e) => setConfig(prev => ({ ...prev, showLocation: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="showLocation" className="text-sm text-gray-700">
                  <SafeIcon icon={FiMapPin} className="inline mr-1" />
                  GPS-Standort in Selfies anzeigen
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showGreeting"
                  checked={config.showGreeting}
                  onChange={(e) => setConfig(prev => ({ ...prev, showGreeting: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="showGreeting" className="text-sm text-gray-700">
                  Grußtext anzeigen
                </label>
              </div>
            </div>
          </div>
        );

      case 'preview':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiEye} className="text-2xl text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ihre App ist bereit!</h3>
              <p className="text-gray-600">
                Hier ist eine Vorschau Ihrer personalisierten CityShot-App.
              </p>
            </div>

            <div className="max-w-sm mx-auto">
              <div className="bg-gray-800 rounded-2xl p-4 aspect-[3/4] relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">Kamera-Vorschau</span>
                </div>

                {/* Preview overlays */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Logo */}
                  {config.logo && (
                    <div
                      style={{
                        position: 'absolute',
                        left: config.logoPosition.x,
                        top: config.logoPosition.y,
                        opacity: config.logoOpacity,
                        height: config.logoSize,
                        width: 'auto'
                      }}
                    >
                      <img 
                        src={config.logo} 
                        alt="Logo" 
                        className="h-full w-auto object-contain"
                      />
                    </div>
                  )}

                  {/* Custom Text */}
                  {config.customText && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '20%',
                        transform: 'translateX(-50%)',
                        opacity: config.textOpacity,
                        fontSize: '12px',
                        fontWeight: config.textStyle.includes('bold') ? 'bold' : 'normal',
                        fontFamily: config.fontFamily,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: config.textBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                        color: config.textColor,
                        textAlign: 'center',
                        maxWidth: '80%'
                      }}
                    >
                      {config.customText}
                    </div>
                  )}

                  {/* Location */}
                  {config.showLocation && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '8%',
                        transform: 'translateX(-50%)',
                        opacity: config.locationOpacity,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: config.textBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                        color: config.locationColor,
                        fontFamily: config.locationFontFamily,
                        textAlign: 'center'
                      }}
                    >
                      {config.showGreeting && (
                        <div style={{ fontSize: '10px', marginBottom: '2px' }}>
                          {config.greetingText}
                        </div>
                      )}
                      <div className="flex items-center justify-center gap-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                        <SafeIcon icon={FiMapPin} />
                        {user?.cityName}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-xl text-center">
              <h4 className="font-semibold text-green-900 mb-2">🎉 Perfekt!</h4>
              <p className="text-green-700 text-sm">
                Ihre CityShot-App ist nun einsatzbereit. Besucher können ab sofort 
                personalisierte Selfies mit Ihrem Branding erstellen.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!user || user.onboarded) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              CityShot Einrichtung
            </h1>
            <span className="text-sm text-gray-500">
              {currentStep} von {steps.length}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                  step.id <= currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step.id < currentStep ? <SafeIcon icon={FiCheck} /> : step.id}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          {renderStepContent()}
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t">
          <div>
            {currentStep > 1 && currentStep < steps.length && (
              <button
                onClick={handleSkip}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <SafeIcon icon={FiSkipForward} />
                Überspringen
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
            )}

            <button
              onClick={currentStep === steps.length ? handleComplete : handleNext}
              disabled={isCompleting}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isCompleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Wird abgeschlossen...
                </>
              ) : currentStep === steps.length ? (
                <>
                  <SafeIcon icon={FiCheck} />
                  Einrichtung abschließen
                </>
              ) : (
                <>
                  Weiter
                  <SafeIcon icon={FiArrowRight} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;